// AddFundingRow.tsx
import DocumentTypeCombobox from "@/components/custom/DocumentTypeCombobox";
import FileDropzone from "@/components/custom/FileDropzone";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { convertToEuro } from "@/libs/amount";
import { Loader2, Paperclip, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import AddFunderDialog from "../AddFunderDialog";
import ContactAdd from "../ContactAdd";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";

type Funder = {
  id: number;
  fullname: string;
  structureName?: string;
  name?: string;
  isSelf?: boolean;
  representants: Array<{
    id: number;
    firstname: string;
    lastname: string;
    email?: string;
    phone?: string;
  }>;
};

type NewFundingForm = {
  financierId: string;
  representantId: string;
  paidAmount: string;
  dateAmount: string;
  paidPjs: number[]; // tableau d'IDs pour l'API
};

function AddFundingRow({
  project, // { id, country: { currency: { id, code, symbol } } }
  currencySelected, // "EUR" ou devise projet
  funders,
  setFunders, // lifté au parent
  onSaved, // (apiRes) => void
  onCancel, // () => void
}: {
  project: any;
  currencySelected: string;
  funders: Funder[];
  setFunders: React.Dispatch<React.SetStateAction<Funder[]>>;
  onSaved: (apiRes: any) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<NewFundingForm>({
    financierId: "",
    representantId: "",
    paidAmount: "",
    dateAmount: "",
    paidPjs: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ====== Devise du projet ======
  const projectId = project?.id;
  const projectCurrency = project?.country?.currency;
  const projectCurrencyId: number | undefined = projectCurrency?.id; // API
  const projectCurrencyCode: string | undefined = projectCurrency?.code; // UI conv.
  const projectCurrencySymbol: string = projectCurrency?.symbol || ""; // UI

  // ====== Upload PJ (contrat) ======
  const [uploadedPjs, setUploadedPjs] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [pjOpen, setPjOpen] = useState(false);
  const [pjType, setPjType] = useState<any>(16);
  const [pjComment, setPjComment] = useState("");
  const [pjFiles, setPjFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();
  const { postData } = usePostData<any>();
  const hidePostActions = useHidePostActions();

  // ====== Ajout Funder / Representant ======
  const [openAddFunderModal, setOpenAddFunderModal] = useState(false);
  const [openAddRepresentantModal, setOpenAddRepresentantModal] =
    useState(false);

  const selectedFunder = useMemo(
    () =>
      form.financierId
        ? funders.find((f) => String(f.id) === String(form.financierId))
        : undefined,
    [funders, form.financierId]
  );
  const selectedRepresentants = selectedFunder?.representants?.length
    ? selectedFunder.representants
    : [];

  const onChangeFunder = (idStr: string) => {
    setForm((f) => ({ ...f, financierId: idStr, representantId: "" }));
  };

  const resetForm = () => {
    setForm({
      financierId: "",
      representantId: "",
      paidAmount: "",
      dateAmount: "",
      paidPjs: [],
    });
    setUploadedPjs([]);
    setErrorMsg(null);
  };

  // ====== Upload PJ handlers ======
  const handleUploadPj = async () => {
    if (pjFiles.length === 0 || !pjType) {
      toast({ title: "Veuillez choisir un type et au moins un fichier." });
      return;
    }
    try {
      setIsUploading(true);
      const data = new FormData();
      data.append("type", "11");
      data.append("subType", pjType);
      data.append("description", pjComment);

      for (const file of pjFiles) {
        let compressed: Blob | null = null;
        try {
          compressed = await handleCompressImage(file, 1024, 768, 0.9);
        } catch {
          compressed = null;
        }
        const toAppend = compressed
          ? new File([compressed], file.name, { type: compressed.type })
          : file;
        data.append("files[]", toAppend);
      }

      const newMedias: any = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (!Array.isArray(newMedias) && newMedias?.success === false) {
        toast({ title: newMedias.message ?? "Upload échoué", duration: 1500 });
        return;
      }

      const metas = (Array.isArray(newMedias) ? newMedias : []).map((m) => ({
        id: m.id,
        name: m.name ?? "Fichier",
      }));
      const ids = metas.map((m) => m.id);

      setForm((f) => ({ ...f, paidPjs: [...(f.paidPjs || []), ...ids] }));
      setUploadedPjs((prev) => [...prev, ...metas]);

      // reset popup
      setPjFiles([]);
      setPjType(16);
      setPjComment("");
      setPjOpen(false);
      toast({ title: "Pièce(s) jointe(s) ajoutée(s).", duration: 1200 });
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedPj = (id: number) => {
    if (isSaving || isUploading) return;
    setUploadedPjs((prev) => prev.filter((p) => p.id !== id));
    setForm((f) => ({
      ...f,
      paidPjs: (f.paidPjs || []).filter(
        (pid: number) => Number(pid) !== Number(id)
      ),
    }));
  };

  const removeSelectedPjFile = (index: number) => {
    if (isUploading) return;
    setPjFiles((files) => files.filter((_, i) => i !== index));
  };

  // ====== Save (API veut paidAmount en EUR) ======
  const handleSave = async () => {
    setErrorMsg(null);
    try {
      setIsSaving(true);

      if (!form.financierId) {
        setErrorMsg("Veuillez choisir un financeur.");
        return;
      }
      const amountNumber = Number(
        (form.paidAmount || "").toString().replace(/\s/g, "").replace(",", ".")
      );
      if (!amountNumber || isNaN(amountNumber)) {
        setErrorMsg("Montant invalide.");
        return;
      }
      if (!projectId || !projectCurrencyId) {
        setErrorMsg("Projet ou devise du projet introuvable.");
        return;
      }

      // Convertir l'UI vers EUR pour l'API
      let amountInEuro = amountNumber;
      if (currencySelected !== "EUR" && projectCurrencyCode) {
        amountInEuro = Number(
          await convertToEuro(amountNumber, projectCurrency)
        );
      }

      const payload = {
        project: projectId,
        financier: form.financierId ? Number(form.financierId) : undefined,
        representant: form.representantId
          ? Number(form.representantId)
          : undefined,
        estimatedAmount: "0",
        estimatedDate: new Date(),
        estimatedDevise: 3,
        paidAmount: amountInEuro, // EUR
        paidDevise: projectCurrencyId, // ID devise projet
        dateAmount: form.dateAmount ? new Date(form.dateAmount) : null,
        pjs: form.paidPjs || [], // tableau d'IDs
      };

      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding/save?by=project&onlyFunding=true`,
        data: payload,
      });

      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        res.success === false
      ) {
        throw new Error(res?.message || "Échec de la sauvegarde.");
      }

      // 👉 On renvoie la réponse API brute au parent, qui mettra la liste à jour
      onSaved(res);

      resetForm();
      onCancel();
      toast({ title: "Financement ajouté." });
    } catch (e: any) {
      setErrorMsg(e?.message || "Erreur lors de la sauvegarde.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving || isUploading) return;
    resetForm();
    onCancel();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSaving && !pjOpen) handleCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSaving, pjOpen]);

  if (hidePostActions) return null;
  return (
    <>
      <TableRow className="text-[#434653] bg-white/50">
        {/* Financeur */}
        <TableCell>
          <div className="flex items-center gap-2">
            <Select
              value={form.financierId}
              onValueChange={onChangeFunder}
              disabled={isSaving || isUploading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choisir un financeur" />
              </SelectTrigger>
              <SelectContent>
                {funders.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.isSelf
                      ? "Moi-même"
                      : f.fullname || f.structureName || f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Ajouter un financeur */}
            <button
              type="button"
              className="px-2 py-1 border rounded text-xs whitespace-nowrap"
              onClick={() => setOpenAddFunderModal(true)}
              disabled={isSaving || isUploading}
              title="Ajouter un financeur"
            >
              +
            </button>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Si vous financez vous-même le projet, ou si vous ne souhaitez pas
            préciser le financeur, choisissez &quot;Moi-même&quot;.
          </p>
        </TableCell>

        {/* Représentant */}
        <TableCell>
          <div className="flex items-center gap-2">
            <Select
              value={form.representantId}
              onValueChange={(val) =>
                setForm((prev) => ({ ...prev, representantId: val }))
              }
              disabled={
                isSaving ||
                isUploading ||
                !form.financierId ||
                selectedRepresentants.length === 0
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    !form.financierId
                      ? "Choisir le financeur d'abord"
                      : selectedRepresentants.length === 0
                      ? "Aucun représentant"
                      : "Choisir un représentant"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {selectedRepresentants.length > 0 &&
                  selectedRepresentants.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.firstname} {r.lastname}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {/* Ajouter un représentant */}
            <button
              type="button"
              className="px-2 py-1 border rounded text-xs whitespace-nowrap disabled:opacity-60"
              onClick={() => setOpenAddRepresentantModal(true)}
              disabled={isSaving || isUploading || !form.financierId}
              title={
                form.financierId
                  ? "Ajouter un représentant"
                  : "Choisir un financeur d'abord"
              }
            >
              +
            </button>
          </div>
        </TableCell>

        {/* Montant */}
        <TableCell>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs opacity-70">
              {currencySelected === "EUR" ? "€" : projectCurrencySymbol}
            </span>
            <input
              type="text"
              inputMode="decimal"
              className="w-full border rounded pl-6 pr-2 py-1 text-sm"
              placeholder="0,00"
              value={form.paidAmount}
              onChange={(e) =>
                setForm((f) => ({ ...f, paidAmount: e.target.value }))
              }
              disabled={isSaving || isUploading}
            />
          </div>
        </TableCell>

        {/* Date */}
        <TableCell>
          <input
            type="date"
            className="w-full border rounded px-2 py-1 text-sm"
            value={form.dateAmount}
            onChange={(e) =>
              setForm((f) => ({ ...f, dateAmount: e.target.value }))
            }
            disabled={isSaving || isUploading}
          />
        </TableCell>

        {/* PJ */}
        <TableCell>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2 py-1 border rounded text-sm"
                onClick={() => setPjOpen(true)}
                disabled={isSaving}
                title="Uploader une pièce jointe"
              >
                <Paperclip className="w-4 h-4" />
                PJ
              </button>
              {Array.isArray(form.paidPjs) && form.paidPjs.length > 0 && (
                <span className="text-xs text-gray-600">
                  {form.paidPjs.length} fichier(s)
                </span>
              )}
            </div>

            {uploadedPjs.length > 0 && (
              <div className="flex flex-wrap gap-2 max-w-[240px]">
                {uploadedPjs.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1 text-xs bg-gray-100 border rounded px-2 py-1"
                    title={p.name}
                  >
                    <span className="max-w-[140px] truncate">{p.name}</span>
                    <button
                      type="button"
                      className="p-0.5 hover:opacity-80"
                      onClick={() => removeUploadedPj(p.id)}
                      disabled={isSaving || isUploading}
                      aria-label={`Retirer ${p.name}`}
                      title="Retirer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </TableCell>

        {/* Actions */}
        <TableCell className="text-right">
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1 border rounded text-sm"
              onClick={handleCancel}
              disabled={isSaving || isUploading}
            >
              Annuler
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded text-sm bg-primary text-white disabled:opacity-60 inline-flex items-center gap-2"
              onClick={handleSave}
              disabled={isSaving || isUploading}
            >
              {(isSaving || isUploading) && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              Save
            </button>
          </div>
        </TableCell>
      </TableRow>

      {errorMsg && (
        <tr>
          <td colSpan={6} className="pt-2 text-sm text-red-600">
            {errorMsg}
          </td>
        </tr>
      )}

      {/* ====== POPUP Upload PJ ====== */}
      <Dialog open={pjOpen} onOpenChange={(o) => !isUploading && setPjOpen(o)}>
        <DialogContent className="max-w-lg">
          <div className="space-y-4">
            <h3 className="text-base font-semibold">
              Ajouter une pièce jointe
            </h3>

            <div>
              <label className="text-sm font-medium">Type de document</label>
              <DocumentTypeCombobox value={pjType} onChange={setPjType} />
            </div>

            <div>
              <label className="text-sm font-medium">Commentaire</label>
              <textarea
                value={pjComment}
                onChange={(e) => setPjComment(e.target.value)}
                className="w-full border p-2 rounded"
                placeholder="Ajouter un commentaire (facultatif)"
                disabled={isUploading}
              />
            </div>

            <div className="space-y-2">
              <FileDropzone
                accept="PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG"
                onFileSelected={setPjFiles}
              />
              {pjFiles?.length > 0 && (
                <ul className="text-xs text-gray-700 space-y-1 max-h-28 overflow-auto">
                  {pjFiles.map((f, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        className="border rounded px-1 py-0.5 hover:bg-gray-50"
                        onClick={() => removeSelectedPjFile(idx)}
                        disabled={isUploading}
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded border text-sm"
                onClick={() => {
                  if (isUploading) return;
                  setPjOpen(false);
                }}
                disabled={isUploading}
              >
                Fermer
              </button>
              <button
                className="px-3 py-2 rounded bg-primary text-white text-sm disabled:opacity-60 inline-flex items-center gap-2"
                onClick={handleUploadPj}
                disabled={isUploading}
              >
                {isUploading && <Loader2 className="w-4 h-4 animate-spin" />}
                Upload
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== MODAL: Ajouter un financeur ====== */}
      <AddFunderDialog
        hasTrigger={false}
        open={openAddFunderModal}
        onHandleOpen={setOpenAddFunderModal}
        onSaveSuccess={(res: Funder) => {
          // 1) Met à jour la liste parent
          setFunders((prev) => [
            ...prev,
            { ...res, representants: res.representants ?? [] },
          ]);
          // 2) Sélectionne par défaut le nouveau financeur
          setForm((prev) => ({
            ...prev,
            financierId: String(res.id),
            representantId: "",
          }));
          // 3) Ferme le modal
          setOpenAddFunderModal(false);
        }}
      />

      {/* ====== MODAL: Ajouter un représentant ====== */}
      <ContactAdd
        open={openAddRepresentantModal}
        setOpen={setOpenAddRepresentantModal}
        financer={form.financierId ? Number(form.financierId) : undefined}
        handleCreate={(newRepresentant: {
          id: number;
          firstname: string;
          lastname: string;
        }) => {
          if (!form.financierId) {
            setOpenAddRepresentantModal(false);
            return;
          }
          const finId = Number(form.financierId);
          // 1) Update parent list immutably
          setFunders((prev) =>
            prev.map((f) =>
              f.id === finId
                ? {
                    ...f,
                    representants: [
                      ...(f.representants || []),
                      newRepresentant,
                    ],
                  }
                : f
            )
          );
          // 2) Ferme le modal
          setOpenAddRepresentantModal(false);
          // 3) Le sélectionne par défaut
          setForm((prev) => ({
            ...prev,
            representantId: String(newRepresentant.id),
          }));
        }}
      />
    </>
  );
}

export default AddFundingRow;
